import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import MFASetup from './MFASetup';
import { Shield, ArrowLeft, Smartphone, Key, Eye, EyeOff, Lock, UserX } from 'lucide-react';

const DEMO_EMAIL = 'demo@paymentpower.net';
const DEMO_PASSWORD = 'AzfQX2i2n2mGL7D+';

const LoginForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  // Check localStorage for redirect in progress (survives page reload)
  const [redirecting, setRedirecting] = useState(() => {
    return localStorage.getItem('login_redirecting') === 'true';
  });
  const [error, setError] = useState('');
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [isAccountDeactivated, setIsAccountDeactivated] = useState(false);
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials', 'mfa', 'mfa_setup'
  const [showBackupCodeInput, setShowBackupCodeInput] = useState(false);
  const { login, backendUrl, logout } = useAuth();
  const { theme } = useTheme();

  // Clear redirect flag after a timeout (in case redirect fails)
  useEffect(() => {
    if (redirecting) {
      const timer = setTimeout(() => {
        localStorage.removeItem('login_redirecting');
        setRedirecting(false);
      }, 10000); // Clear after 10 seconds if still on login page
      return () => clearTimeout(timer);
    }
  }, [redirecting]);

  // Auto-fill password for demo account
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Auto-fill password when demo email is entered
    if (newEmail.toLowerCase() === DEMO_EMAIL.toLowerCase()) {
      setPassword(DEMO_PASSWORD);
    }
  };

  // Clear any stale session data when login form loads
  useEffect(() => {
    console.log('[LoginForm] Component mounted');
    // Only clear error and MFA-related state, not credentials
    // User might have pre-filled credentials or browser autocomplete
    setError('');
    setTotpCode('');
    setBackupCode('');
    setLoginStep('credentials');
    setShowBackupCodeInput(false);
  }, []);

  const handleInitialLogin = async (e) => {
    e.preventDefault();
    
    // Prevent double-click
    if (loading) {
      console.log('[LoginForm] Login already in progress, ignoring duplicate click');
      return;
    }
    
    setLoading(true);
    setError('');

    console.log('[LoginForm] Attempting initial login');
    console.log('[LoginForm] Backend URL:', backendUrl);
    console.log('[LoginForm] Email:', email);
    console.log('[LoginForm] Form state:', { email, password: '***', loading });
    
    if (!backendUrl) {
      setLoading(false);
      setError('Backend URL is not configured. Please refresh the page.');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/mfa/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      console.log('[LoginForm] Login response status:', response.status);
      
      // Check if response is valid JSON
      let result;
      try {
        result = await response.json();
        console.log('[LoginForm] Login response data:', result);
      } catch (jsonError) {
        console.error('[LoginForm] Failed to parse response as JSON:', jsonError);
        throw new Error('Invalid response from server. Please try again.');
      }

      if (response.ok) {
        if (result.requires_mfa_setup) {
          // Store token for MFA setup (session-based)
          sessionStorage.setItem('temp_token', result.access_token);
          setLoginStep('mfa_setup');
          setLoading(false);
        } else if (result.requires_mfa) {
          setLoginStep('mfa');
          setLoading(false);
        } else {
          // Complete login - store token, update context, and navigate to dashboard
          // Set localStorage flag to persist redirecting state across page reload
          console.log('[LoginForm] Login successful, storing token and navigating to dashboard');
          localStorage.setItem('login_redirecting', 'true');
          setRedirecting(true);
          sessionStorage.setItem('token', result.access_token);
          
          // Get user's preferred default screen or fallback to /dashboard
          const defaultScreen = result.user?.default_screen || '/dashboard';
          console.log('[LoginForm] Navigating to default screen:', defaultScreen);
          
          // IMPORTANT: Force re-render by reloading the page
          // This ensures AuthContext picks up the new token from localStorage
          window.location.href = defaultScreen;
          // Don't set loading to false - keep button disabled during redirect
          return;
        }
      } else {
        console.error('[LoginForm] Login failed:', result);
        // Inspect 403 detail to surface the right account-state banner
        const detail = result.detail || '';
        const isLockedMsg = response.status === 403 && /locked|blocked/i.test(detail);
        const isDeactivatedMsg = response.status === 403 && /deactivated|inactive/i.test(detail);
        if (isLockedMsg) {
          setIsAccountLocked(true);
          setIsAccountDeactivated(false);
          setError(detail);
        } else if (isDeactivatedMsg) {
          setIsAccountLocked(false);
          setIsAccountDeactivated(true);
          setError(detail);
        } else {
          setIsAccountLocked(false);
          setIsAccountDeactivated(false);
          setError(detail || 'Invalid credentials');
        }
      }
    } catch (err) {
      console.error('[LoginForm] Login error:', err);
      
      // Determine error message based on error type
      if (err.message && err.message.includes('Invalid response')) {
        setError(err.message);
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
        setError('Network error: Unable to reach the server. Please try again.');
      } else {
        setError(err.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMFALogin = async (e) => {
    e.preventDefault();
    
    // Prevent double-click
    if (loading) {
      console.log('[LoginForm] MFA verification already in progress, ignoring duplicate click');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/mfa/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          totp_code: showBackupCodeInput ? undefined : totpCode,
          backup_code: showBackupCodeInput ? backupCode : undefined,
          remember_device: rememberDevice
        })
      });

      // Check if response is valid JSON
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('[LoginForm] Failed to parse MFA response as JSON:', jsonError);
        throw new Error('Invalid response from server. Please try again.');
      }

      if (response.ok) {
        // Set localStorage flag to persist redirecting state across page reload
        console.log('[LoginForm] MFA verification successful, navigating to dashboard');
        localStorage.setItem('login_redirecting', 'true');
        setRedirecting(true);
        sessionStorage.setItem('token', result.access_token);
        
        // Get user's preferred default screen or fallback to /dashboard
        const defaultScreen = result.user?.default_screen || '/dashboard';
        console.log('[LoginForm] Navigating to default screen:', defaultScreen);
        
        // IMPORTANT: Force re-render by reloading the page
        // This ensures AuthContext picks up the new token from localStorage
        window.location.href = defaultScreen;
        // Don't set loading to false - keep button disabled during redirect
        return;
      } else {
        // Check if account is locked (status 403)
        if (response.status === 403 && result.detail && result.detail.includes('Account is locked')) {
          setError(result.detail);
          // Return to login screen after account lockout
          setTimeout(() => {
            backToLogin();
          }, 3000);
        } else {
          setError(result.detail || 'Invalid MFA code');
        }
      }
    } catch (err) {
      console.error('[LoginForm] MFA login error:', err);
      
      // Determine error message based on error type
      if (err.message && err.message.includes('Invalid response')) {
        setError(err.message);
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
        setError('Network error: Unable to reach the server. Please try again.');
      } else {
        setError(err.message || 'Network error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetupComplete = () => {
    // Remove temp token and redirect to login
    sessionStorage.removeItem('temp_token');
    setLoginStep('credentials');
    setError('');
    // Show success message
    alert('MFA has been successfully enabled! Please log in again.');
  };

  const backToLogin = () => {
    setLoginStep('credentials');
    setError('');
    setTotpCode('');
    setBackupCode('');
    setShowBackupCodeInput(false);
  };

  // Show redirecting overlay when login is successful
  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center dashboard-background">
        <Card className="w-full max-w-md shadow-2xl border-0 rounded-2xl">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-lg font-medium text-gray-700">Signing you in...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we redirect you</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loginStep === 'mfa_setup') {
    return (
      <div className="min-h-screen flex items-center justify-center dashboard-background">
        <div className="w-full max-w-4xl px-4">
          <MFASetup 
            onComplete={handleMFASetupComplete}
            onBack={backToLogin}
          />
        </div>
      </div>
    );
  }

  if (loginStep === 'mfa') {
    return (
      <div className="min-h-screen flex items-center justify-center dashboard-background">
        <Card className="w-full max-w-md shadow-2xl border-0 rounded-2xl">
          <CardHeader className="text-center pb-6 pt-8">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-bold mb-2">Two-Factor Authentication</CardTitle>
            <p className="text-gray-600">Enter your authentication code to continue</p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleMFALogin} className="space-y-6">
              {!showBackupCodeInput ? (
                <div>
                  <label htmlFor="totp-code" className="block text-sm font-semibold text-gray-700 mb-2">
                    <Smartphone className="h-4 w-4 inline mr-2" />
                    Authenticator Code
                  </label>
                  <input
                    type="text"
                    id="totp-code"
                    value={totpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTotpCode(value);
                      if (error) setError('');
                    }}
                    maxLength={6}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-center text-2xl font-mono tracking-wider"
                    autoComplete="off"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="backup-code" className="block text-sm font-semibold text-gray-700 mb-2">
                    <Key className="h-4 w-4 inline mr-2" />
                    Backup Code
                  </label>
                  <input
                    type="text"
                    id="backup-code"
                    value={backupCode}
                    onChange={(e) => {
                      setBackupCode(e.target.value.toUpperCase());
                      if (error) setError('');
                    }}
                    maxLength={8}
                    placeholder="XXXXXXXX"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-center text-lg font-mono tracking-wider"
                    autoComplete="off"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter one of your backup codes (each can only be used once)
                  </p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember-device"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-device" className="ml-2 text-sm text-gray-700">
                  Remember this device for 24 hours
                </label>
              </div>

              {error && (
                <div className={`border rounded-lg p-4 ${
                  error.includes('Account is locked') 
                    ? 'bg-orange-50 border-orange-300' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`text-sm font-semibold ${
                    error.includes('Account is locked') 
                      ? 'text-orange-700' 
                      : 'text-red-600'
                  }`}>
                    {error.includes('Account is locked') && '🔒 '}
                    {error}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={loading || (!showBackupCodeInput && totpCode.length !== 6) || (showBackupCodeInput && backupCode.length !== 8)}
                  className="w-full py-3 text-base font-semibold"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Verifying...
                    </>
                  ) : (
                    'Verify & Continue'
                  )}
                </Button>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowBackupCodeInput(!showBackupCodeInput)}
                    className="flex-1 px-4 py-2 text-sm border rounded-lg transition-colors"
                    style={{ 
                      color: theme.linkColor,
                      borderColor: theme.borderColor
                    }}
                    onMouseEnter={(e) => e.target.style.color = theme.linkHoverColor}
                    onMouseLeave={(e) => e.target.style.color = theme.linkColor}
                  >
                    {showBackupCodeInput ? 'Use Authenticator Code' : 'Use Backup Code'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={backToLogin}
                    className="px-4 py-2 text-sm border rounded-lg transition-colors"
                    style={{ 
                      backgroundColor: theme.buttonSecondaryColor,
                      borderColor: theme.borderColor,
                      color: theme.textColor
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = theme.hoverBackgroundColor}
                    onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonSecondaryColor}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1 inline" />
                    Back
                  </button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center dashboard-background">
      <div className="w-full max-w-md">
      <Card className="w-full shadow-2xl border-0 rounded-2xl">
        <CardHeader className="text-center pb-6 pt-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <CardTitle className="text-3xl font-bold">PayGateway Pro</CardTitle>
          </div>
          <p className="text-gray-600 text-lg">Sign in to access your dashboard</p>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleInitialLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-base"
                placeholder="demo@paymentpower.net"
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-base"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className={`border rounded-lg p-4 ${
                isAccountLocked 
                  ? 'bg-orange-50 border-orange-300'
                  : isAccountDeactivated
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-red-50 border-red-200'
              }`} data-testid="login-error-banner">
                {isAccountLocked ? (
                  <div className="flex items-start gap-3">
                    <Lock className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-orange-700">Account Blocked</p>
                      <p className="text-sm text-orange-600 mt-1">{error}</p>
                      <p className="text-xs text-orange-500 mt-2">
                        Please contact an administrator to unblock your account.
                      </p>
                    </div>
                  </div>
                ) : isAccountDeactivated ? (
                  <div className="flex items-start gap-3" data-testid="login-deactivated-banner">
                    <UserX className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-700">Account Deactivated</p>
                      <p className="text-sm text-amber-600 mt-1">{error}</p>
                      <p className="text-xs text-amber-500 mt-2">
                        Your access has been disabled. Please reach out to an administrator to re-activate it.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-red-600">{error}</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 text-base font-semibold"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
            
            {/* Demo Credentials Note */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-800 mb-2">Demo Access</p>
              <div className="text-sm text-blue-700 space-y-1">
                <p><span className="font-medium">Email:</span> {DEMO_EMAIL}</p>
                <p><span className="font-medium">Password:</span> {DEMO_PASSWORD}</p>
              </div>
              <p className="text-xs text-blue-600 mt-2 italic">
                Tip: Enter the demo email above and the password will auto-fill.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
      <a href="https://www.paymentpower.net" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 mt-4 text-gray-400 hover:text-gray-600 transition-colors text-sm">
        <img src="https://customer-assets.emergentagent.com/job_3de5042f-38bc-4c59-be9d-105ed5dfd1b8/artifacts/gshoc85d_favicon.png" alt="" className="w-5 h-5" />
        by Payment Power
      </a>
      </div>
    </div>
  );
};

export default LoginForm;